import type {ContentItem, QuizType} from "@khan/content-types";
import type {Location} from "@khan/urls";

import formatQueryString from "#shared-package/format-query-string.ts";

export const getModalUrlForContent = (
    contentItem: ContentItem,
    location: Location,
): string => {
    // We no longer use modals for content.
    // TODO (LP-13072) Clean up this function
    return contentItem.nodeUrl;
};

export const stripModalFromRoute = (
    path: string,
    queryParams: Record<string, string>,
): string => {
    // The modal routes are subroutes.  They are slightly different,
    // so they have two different processors:

    // For video/exercise/article pages, the path strips out the lesson and beyond
    // From: /domain/course/unit/lesson/type(v|e|a)/slug?modal=1
    // To:   /domain/course/unit
    let newPathname = path.replace(/\/[^/]*\/(v|e|a)\/.+/gi, "");

    // For quiz/unit test, there isn't the extra "lesson" to remove
    // From: /domain/course/unit/type(quiz|test)/slug?modal=1
    // To:   /domain/course/unit
    newPathname = newPathname.replace(/\/(quiz|test)\/.+/gi, "");

    // Strip the modal parameter from the queryParams
    const newQueryParams = {...queryParams} as const;
    // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: string; }' only permits reading.
    delete newQueryParams.modal;

    const newSearch = formatQueryString(newQueryParams, true);

    return newPathname + newSearch;
};

const getPathname = (pathname: string) => {
    // For all other types, we want to open in the modal.
    // This is made trickier by the fact that modal URLs are "appended" to the
    // topic page URL; we want to shove some junk onto the existing URL so that
    // the Route knows to pop the modal.
    // If the current pathname already is a content modal, strip it out
    // This can happen if the user clicks twice in rapid succession.
    // This can also happen if a modal content item links to a different
    // content-item, like "Next Video"
    const contentModalRegex = /\/[^/]+\/(v|e|a)\/.+/gi;
    const testModalRegex = /\/(test|quiz)\/.+/gi;
    if (pathname.match(contentModalRegex)) {
        pathname = pathname.replace(contentModalRegex, "");
    } else if (pathname.match(testModalRegex)) {
        pathname = pathname.replace(testModalRegex, "");
    }

    // Strip the trailing slash if present.
    if (pathname[pathname.length - 1] === "/") {
        pathname = pathname.slice(0, pathname.length - 1);
    }

    return pathname;
};

export const getModalUrlForTestOrQuiz = (
    {
        type,
        slug,
    }: {
        type: QuizType;
        slug: string;
    },
    location: Location,
    // An optional referrer string to include in the link for analytics.
    referrer?: string,
): string => {
    const currentPathname = getPathname(location.pathname);

    const queryParams = {
        ...location.queryParams,
        ...(referrer ? {referrer} : null),
    } as const;

    if (type === "subject-challenge") {
        type = "test";
        slug = "subject-challenge";
        // TODO (LP-13076) Remove this once we've migrated course challenge to 321
        // @ts-expect-error - TS2339 - Property 'modal' does not exist on type '{ readonly referrer?: string | undefined; }'.
        queryParams.modal = "1";
    } else if (type === "mastery-challenge") {
        type = "test";
        slug = "mastery-challenge";
        // TODO (LP-13076) Remove this once we've migrated mastery challenge to 321
        // @ts-expect-error - TS2339 - Property 'modal' does not exist on type '{ readonly referrer?: string | undefined; }'.
        queryParams.modal = "1";
    }

    const queryString = formatQueryString(
        queryParams,
        /* include question mark */ true,
    );

    return `${currentPathname}/${type}/${slug}${queryString}`;
};
